/**
 * Pure model behind the "LLM Config" page: turns the dashboard API's
 * `GET /config/llm-models` payload into the rows the page draws. No React.
 *
 * Nothing here names a model. The configured side is the signed runtime
 * config, the observed side is the cost ledger of the newest Echoes; this
 * module only joins the two through the step registry.
 *
 * Environment naming: customer production is the literal key `stg`;
 * engineering staging is the literal key `staging`.
 */
import { channelForProvider } from "../costs/fundingSource";
import {
  PHASES,
  assignmentTarget,
  humanizeId,
  ledgerStepId,
  stepInfo,
  type PhaseId,
} from "./stepRegistry";

export type ModelRef = {
  route: string | null;
  provider: string | null;
  model: string;
  effort: string | null;
};

export type LlmAssignment = {
  config_key: string;
  service: string;
  inactive: boolean;
  /** JSON path inside the config value; "" is the value itself. */
  path: string;
  primary: ModelRef;
  fallbacks: ModelRef[];
};

export type LlmOverrideChange = {
  path: string;
  primary: ModelRef;
  fallbacks: ModelRef[];
};

export type LlmOverride = {
  rule_id: string | null;
  config_key: string;
  effect: string | null;
  priority: number | null;
  basis_points: number | null;
  variant: string | null;
  product_flows: string[];
  skills: string[];
  cohort_ids: string[];
  subject_count: number;
  agency_count: number;
  inactive: boolean;
  assignment_count: number;
  changes: LlmOverrideChange[];
};

export type ObservedStep = {
  domain: string;
  step: string;
  provider: string;
  model: string;
  billing_channels: string[];
  calls: number;
  usd: number;
  unpriced_calls: number;
  sessions: number;
  first_seen: string | null;
  last_seen: string | null;
};

export type LlmObserved = {
  window: { hours: number; from: string; to: string };
  sessions_requested: number;
  sessions_read: number;
  truncated: boolean;
  steps: ObservedStep[];
};

export type LlmRelease = {
  id: string | null;
  sequence: number | null;
  created_at: string | null;
  catalog_version: string | null;
  digest: string | null;
};

export type LlmModelsResponse = {
  environment: string;
  generated_at: string;
  release: LlmRelease | null;
  assignments: LlmAssignment[];
  overrides: LlmOverride[];
  observed: LlmObserved | null;
  notes: string[];
};

export type StepStatus =
  | "as_configured"
  /** The primary and a configured fallback both answered in the newest run. */
  | "primary_and_fallback"
  | "fallback"
  | "different"
  | "not_seen"
  | "code_default"
  | "off"
  /** A config value this page cannot link to a ledger step: not compared. */
  | "not_linked"
  /** The ledger half of the payload is missing: nothing to compare with. */
  | "ledger_unavailable"
  /** The config half of the payload is missing: nothing to compare with. */
  | "config_unavailable";

export const STATUS_LABELS: Record<StepStatus, string> = {
  as_configured: "As configured",
  primary_and_fallback: "Primary, fallback also answering",
  fallback: "Fallback answering",
  different: "Different from config",
  not_seen: "Not seen in window",
  code_default: "Code default (no config key)",
  off: "Off in config",
  not_linked: "No ledger step linked",
  ledger_unavailable: "Ledger unavailable",
  config_unavailable: "Config unavailable",
};

export type ObservedModel = {
  provider: string;
  model: string;
  /** Recorded billing channels, else the one derived from the provider. */
  channels: string[];
  calls: number;
  usd: number;
  unpricedCalls: number;
  sessions: number;
  firstSeen: string | null;
  lastSeen: string | null;
};

export type StepRow = {
  /** Ledger step id, or `config:<config_key>` for an unmapped config value. */
  id: string;
  label: string;
  phase: PhaseId;
  /** False when the step registry does not know this step or config value. */
  known: boolean;
  /** Every runtime-config assignment feeding this step, inactive ones too. */
  assignments: LlmAssignment[];
  /**
   * The observed model with the latest `last_seen`. When the configured
   * primary and a fallback last ran within minutes of each other (one Echo
   * using both), the primary.
   */
  current: ObservedModel | null;
  /** Other models seen in the window, newest first. */
  alsoSeen: ObservedModel[];
  status: StepStatus;
  /** One line explaining a status that is not obvious from the columns. */
  detail: string | null;
};

export type PhaseGroup = { id: PhaseId; label: string; rows: StepRow[] };

export type OverrideRow = {
  ruleId: string;
  configKey: string;
  effect: string;
  priority: number | null;
  variant: string | null;
  /** Percentage of Echoes for a percentage rule (basis points / 100). */
  percent: number | null;
  /** Off in its own policy, or a percentage rule at 0%. */
  inactive: boolean;
  inactiveReason: string | null;
  /** The base value is off and this rule's value is not: it turns it on. */
  turnsPolicyOn: boolean;
  /** Who the rule applies to: flows, skills, cohorts, counts. Never a hash. */
  audience: string[];
  /** Step labels of the models it changes. */
  steps: string[];
  changes: LlmOverrideChange[];
  assignmentCount: number;
};

export type ReferenceRow = LlmAssignment & {
  /** Labels of the steps this assignment feeds; empty when unmapped. */
  steps: string[];
  referenceOnly: boolean;
};

export type LlmConfigView = {
  environment: string;
  generatedAt: string;
  release: LlmRelease | null;
  observed: Omit<LlmObserved, "steps"> | null;
  phases: PhaseGroup[];
  overrides: OverrideRow[];
  references: ReferenceRow[];
  notes: string[];
  counts: Record<StepStatus, number>;
  /** Observed steps the registry does not know (shown under "other"). */
  unmappedObservedSteps: string[];
  /** `config_key path` of assignments the registry does not map to a step. */
  unmappedAssignments: string[];
};

/** Literal environment key with its product meaning. */
export function environmentLabel(environment: string): string {
  if (environment === "stg") return "Production (literal key stg)";
  if (environment === "staging")
    return "Engineering staging (literal key staging)";
  return `Environment ${environment || "unknown"}`;
}

const DATE_SUFFIX = /^-(\d{4}-\d{2}-\d{2}|\d{6,8})$/;

function isDatedVariant(dated: string, base: string): boolean {
  return dated.startsWith(base) && DATE_SUFFIX.test(dated.slice(base.length));
}

/**
 * Whether an observed model id is the configured model. The ledger can carry
 * a dated id (`gpt-5.5-2026-04-23`) where the config names the family
 * (`gpt-5.5`), so a date suffix on either side still matches. Providers are
 * not compared: the config and the ledger name them differently
 * (`openai_image` / `openai`, `google` / `gemini`).
 */
export function sameModel(
  observed: string | null | undefined,
  configured: string | null | undefined,
): boolean {
  const left = (observed ?? "").trim().toLowerCase();
  const right = (configured ?? "").trim().toLowerCase();
  if (!left || !right) return false;
  return (
    left === right || isDatedVariant(left, right) || isDatedVariant(right, left)
  );
}

function toObservedModel(row: ObservedStep): ObservedModel {
  const recorded = (row.billing_channels ?? []).filter(Boolean);
  return {
    provider: row.provider,
    model: row.model,
    channels: recorded.length ? recorded : [channelForProvider(row.provider)],
    calls: Number(row.calls) || 0,
    usd: Number(row.usd) || 0,
    unpricedCalls: Number(row.unpriced_calls) || 0,
    sessions: Number(row.sessions) || 0,
    firstSeen: row.first_seen ?? null,
    lastSeen: row.last_seen ?? null,
  };
}

/** Newest `last_seen` first; ties go to the busier model. */
function byRecency(a: ObservedModel, b: ObservedModel): number {
  const left = a.lastSeen ?? "";
  const right = b.lastSeen ?? "";
  if (left !== right) return left < right ? 1 : -1;
  return b.calls - a.calls;
}

/**
 * One Echo runs a step within minutes. Two models whose newest rows are this
 * close were answering side by side, not one after the other.
 */
const SIDE_BY_SIDE_MS = 10 * 60 * 1000;

function ranSideBySide(a: ObservedModel, b: ObservedModel): boolean {
  const left = Date.parse(a.lastSeen ?? "");
  const right = Date.parse(b.lastSeen ?? "");
  return (
    Number.isFinite(left) &&
    Number.isFinite(right) &&
    Math.abs(left - right) <= SIDE_BY_SIDE_MS
  );
}

/**
 * What is running now, from a step's observed models (newest first). The
 * newest row alone would let a few microseconds decide between the primary and
 * a fallback that one Echo used together (a repair pass, a retried call).
 */
function pickCurrent(
  models: ObservedModel[],
  assignments: LlmAssignment[],
): { current: ObservedModel | null; fallbackAlongside: ObservedModel | null } {
  const newest = models[0] ?? null;
  if (!newest) return { current: null, fallbackAlongside: null };
  const active = assignments.filter((assignment) => !assignment.inactive);
  const isPrimary = (model: ObservedModel) =>
    active.some((assignment) =>
      sameModel(model.model, assignment.primary.model),
    );
  const isFallback = (model: ObservedModel) =>
    active.some((assignment) =>
      assignment.fallbacks.some((fallback) =>
        sameModel(model.model, fallback.model),
      ),
    );
  const near = models.filter(
    (model) => model !== newest && ranSideBySide(model, newest),
  );
  if (isPrimary(newest))
    return {
      current: newest,
      fallbackAlongside: near.find(isFallback) ?? null,
    };
  if (isFallback(newest)) {
    const primary = near.find(isPrimary);
    if (primary) return { current: primary, fallbackAlongside: newest };
  }
  return { current: newest, fallbackAlongside: null };
}

type StatusContext = {
  /** Which halves of the payload arrived. */
  config: boolean;
  ledger: boolean;
  /** When the release in the payload was published. */
  publishedAt: string | null;
};

function resolveStatus(
  assignments: LlmAssignment[],
  current: ObservedModel | null,
  context: StatusContext,
  /** False for a config value no ledger step is known for. */
  linked: boolean,
  /** A configured fallback that last ran within minutes of the primary. */
  fallbackAlongside: ObservedModel | null = null,
): { status: StepStatus; detail: string | null } {
  const active = assignments.filter((assignment) => !assignment.inactive);
  if (!assignments.length) {
    return context.config
      ? { status: "code_default", detail: null }
      : { status: "config_unavailable", detail: null };
  }
  if (!active.length) {
    if (!current) return { status: "off", detail: null };
    return {
      status: "different",
      detail:
        "The base config says off, yet this step ran in the window: a cohort override or a code default.",
    };
  }
  if (!linked) {
    return {
      status: "not_linked",
      detail:
        "This page does not know which ledger step this config value feeds, so nothing is compared.",
    };
  }
  if (!context.ledger) return { status: "ledger_unavailable", detail: null };
  if (!current) return { status: "not_seen", detail: null };
  if (
    active.some((assignment) =>
      sameModel(current.model, assignment.primary.model),
    )
  )
    return fallbackAlongside
      ? {
          status: "primary_and_fallback",
          detail: `The fallback ${fallbackAlongside.model} last answered within ten minutes of the primary: some calls fall back or go through a repair pass.`,
        }
      : { status: "as_configured", detail: null };
  // An Echo keeps the release it started with: a mismatch whose newest ledger
  // row predates the publish says nothing about Echoes that start now.
  const seen = Date.parse(current.lastSeen ?? "");
  const published = Date.parse(context.publishedAt ?? "");
  const detail =
    Number.isFinite(seen) && Number.isFinite(published) && seen < published
      ? "Last ran before this release was published: no Echo has run this step since."
      : null;
  if (
    active.some((assignment) =>
      assignment.fallbacks.some((fallback) =>
        sameModel(current.model, fallback.model),
      ),
    )
  )
    return { status: "fallback", detail };
  return { status: "different", detail };
}

function configRowLabel(configKey: string): string {
  const [service, ...rest] = configKey.split(".");
  return `${humanizeId(rest.join(" ") || service)} (${service} config)`;
}

function audienceOf(rule: LlmOverride): string[] {
  const audience: string[] = [];
  if (rule.product_flows?.length)
    audience.push(
      `Flows: ${rule.product_flows.map((flow) => flow.replaceAll("_", " ")).join(", ")}`,
    );
  if (rule.skills?.length) audience.push(`Skills: ${rule.skills.join(", ")}`);
  if (rule.cohort_ids?.length)
    audience.push(`Cohorts: ${rule.cohort_ids.join(", ")}`);
  const subjects = Number(rule.subject_count) || 0;
  const agencies = Number(rule.agency_count) || 0;
  if (subjects)
    audience.push(`${subjects} ${subjects === 1 ? "subject" : "subjects"}`);
  if (agencies)
    audience.push(`${agencies} ${agencies === 1 ? "agency" : "agencies"}`);
  return audience;
}

function toOverrideRow(
  rule: LlmOverride,
  observedSteps: readonly string[],
  references: readonly ReferenceRow[],
): OverrideRow {
  const isPercentage = rule.effect === "percentage";
  const percent =
    isPercentage && typeof rule.basis_points === "number"
      ? rule.basis_points / 100
      : null;
  const zeroPercent = isPercentage && !(percent && percent > 0);
  const steps = new Set<string>();
  for (const change of rule.changes ?? []) {
    const target = assignmentTarget(
      rule.config_key,
      change.path,
      observedSteps,
    );
    for (const id of target?.steps ?? []) steps.add(stepInfo(id).label);
  }
  // A rule that changes no model still names the steps its config key feeds.
  if (!steps.size) {
    for (const reference of references) {
      if (reference.config_key !== rule.config_key || reference.referenceOnly)
        continue;
      for (const label of reference.steps) steps.add(label);
    }
  }
  const base = references.filter(
    (reference) => reference.config_key === rule.config_key,
  );
  return {
    ruleId: rule.rule_id ?? "(no rule id)",
    configKey: rule.config_key,
    effect: rule.effect ?? "unknown",
    priority: rule.priority ?? null,
    variant: rule.variant ?? null,
    percent,
    inactive: Boolean(rule.inactive) || zeroPercent,
    inactiveReason: rule.inactive
      ? "The rule's own policy says off"
      : zeroPercent
        ? "Percentage rule at 0%: no Echo gets it"
        : null,
    turnsPolicyOn:
      !rule.inactive &&
      base.length > 0 &&
      base.every((reference) => reference.inactive),
    audience: audienceOf(rule),
    steps: [...steps],
    changes: rule.changes ?? [],
    assignmentCount: Number(rule.assignment_count) || 0,
  };
}

/** Rows, overrides and references for the page, from one API payload. */
export function buildLlmConfigView(payload: LlmModelsResponse): LlmConfigView {
  const assignments = payload.assignments ?? [];
  const observedRows = payload.observed?.steps ?? [];
  const context: StatusContext = {
    config: payload.release != null,
    ledger: payload.observed != null,
    publishedAt: payload.release?.created_at ?? null,
  };

  const observedByStep = new Map<string, ObservedModel[]>();
  for (const row of observedRows) {
    const id = ledgerStepId(row.domain, row.step);
    const list = observedByStep.get(id) ?? [];
    list.push(toObservedModel(row));
    observedByStep.set(id, list);
  }
  const observedIds = [...observedByStep.keys()];

  const assignmentsByRow = new Map<string, LlmAssignment[]>();
  const references: ReferenceRow[] = [];
  const unmappedAssignments: string[] = [];
  const attach = (rowId: string, assignment: LlmAssignment) => {
    const list = assignmentsByRow.get(rowId) ?? [];
    list.push(assignment);
    assignmentsByRow.set(rowId, list);
  };
  for (const assignment of assignments) {
    const target = assignmentTarget(
      assignment.config_key,
      assignment.path,
      observedIds,
    );
    references.push({
      ...assignment,
      steps: (target?.steps ?? []).map((id) => stepInfo(id).label),
      referenceOnly: Boolean(target?.referenceOnly),
    });
    if (!target) {
      unmappedAssignments.push(
        `${assignment.config_key}${assignment.path ? ` ${assignment.path}` : ""}`,
      );
      // One row per unmapped config value, however many models it names.
      attach(`config:${assignment.config_key}`, assignment);
      continue;
    }
    if (target.referenceOnly) continue;
    for (const id of target.steps) attach(id, assignment);
  }

  const rowIds = new Set([...observedIds, ...assignmentsByRow.keys()]);
  const rows: (StepRow & { order: number })[] = [];
  for (const id of rowIds) {
    const isConfigRow = id.startsWith("config:");
    const info = isConfigRow ? null : stepInfo(id);
    const models = [...(observedByStep.get(id) ?? [])].sort(byRecency);
    const rowAssignments = assignmentsByRow.get(id) ?? [];
    const { current, fallbackAlongside } = pickCurrent(models, rowAssignments);
    rows.push({
      id,
      label: info ? info.label : configRowLabel(id.slice("config:".length)),
      phase: info ? info.phase : "other",
      known: info ? info.known : false,
      order: info ? info.order : Number.MAX_SAFE_INTEGER,
      assignments: rowAssignments,
      current,
      alsoSeen: models.filter((model) => model !== current),
      ...resolveStatus(
        rowAssignments,
        current,
        context,
        !isConfigRow,
        fallbackAlongside,
      ),
    });
  }
  rows.sort(
    (a, b) =>
      a.order - b.order ||
      Number(a.id.startsWith("config:")) - Number(b.id.startsWith("config:")) ||
      a.label.localeCompare(b.label),
  );

  const counts = Object.fromEntries(
    (Object.keys(STATUS_LABELS) as StepStatus[]).map((status) => [status, 0]),
  ) as Record<StepStatus, number>;
  for (const row of rows) counts[row.status] += 1;

  return {
    environment: payload.environment,
    generatedAt: payload.generated_at,
    release: payload.release ?? null,
    observed: payload.observed
      ? {
          window: payload.observed.window,
          sessions_requested: payload.observed.sessions_requested,
          sessions_read: payload.observed.sessions_read,
          truncated: payload.observed.truncated,
        }
      : null,
    phases: PHASES.map((phase) => ({
      ...phase,
      rows: rows
        .filter((row) => row.phase === phase.id)
        .map(({ order: _order, ...row }) => row),
    })).filter((phase) => phase.rows.length > 0),
    overrides: (payload.overrides ?? []).map((rule) =>
      toOverrideRow(rule, observedIds, references),
    ),
    references,
    notes: (payload.notes ?? []).filter(Boolean),
    counts,
    unmappedObservedSteps: observedIds
      .filter((id) => !stepInfo(id).known)
      .sort(),
    unmappedAssignments,
  };
}

export type ConfiguredGroup = {
  primary: ModelRef;
  fallbacks: ModelRef[];
  inactive: boolean;
  /** `config_key → path` of every assignment that says exactly this. */
  sources: string[];
};

const modelKey = (model: ModelRef) =>
  [model.provider ?? "", model.model, model.effort ?? ""].join("|");

/**
 * A step's assignments with identical models merged (the two voice render
 * profiles that both say `eleven_v3` read as one line with two sources).
 */
export function groupAssignments(
  assignments: LlmAssignment[],
): ConfiguredGroup[] {
  const groups = new Map<string, ConfiguredGroup>();
  for (const assignment of assignments) {
    const key = [
      assignment.inactive ? "off" : "on",
      modelKey(assignment.primary),
      ...assignment.fallbacks.map(modelKey),
    ].join("||");
    const group = groups.get(key) ?? {
      primary: assignment.primary,
      fallbacks: assignment.fallbacks,
      inactive: assignment.inactive,
      sources: [],
    };
    group.sources.push(describeSource(assignment));
    groups.set(key, group);
  }
  // Active assignments first: they are what an Echo starting now gets.
  return [...groups.values()].sort(
    (a, b) => Number(a.inactive) - Number(b.inactive),
  );
}

/**
 * "google · Vertex AI" for a ledger provider and its billing channel labels;
 * just "OpenAI" when the two say the same thing ("openai · OpenAI").
 */
export function providerLine(
  provider: string,
  channelLabels: string[],
): string {
  const channels = channelLabels.join(", ");
  if (!channels) return provider;
  return channels.toLowerCase() === provider.toLowerCase()
    ? channels
    : `${provider} · ${channels}`;
}

/** "gemini-3.8-flash · google · medium": one configured model on one line. */
export function describeModel(model: ModelRef): string {
  return [model.model, model.provider, model.effort]
    .filter(Boolean)
    .join(" · ");
}

/** "orchestrator.llm_routes.v1 → i2v_prompt_director" for the muted line. */
export function describeSource(assignment: {
  config_key: string;
  path: string;
}): string {
  return assignment.path
    ? `${assignment.config_key} → ${assignment.path}`
    : assignment.config_key;
}

/** "2026-10-02 10:14 UTC" from an ISO instant; the raw text when unparseable. */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) return "unknown";
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return iso;
  return `${new Date(time).toISOString().slice(0, 16).replace("T", " ")} UTC`;
}

export function formatModelUsd(usd: number): string {
  if (!(usd > 0)) return "$0.00";
  if (usd < 0.01) return "<$0.01";
  return `$${usd.toFixed(2)}`;
}
